import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  User, Lock, Bell, Trash2, Save, Loader2, AlertCircle, CheckCircle2,
  X, ShieldAlert, Eye, EyeOff, Brain, ChevronRight,
} from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import ViewNav from "@/components/nav/ViewNav";
import ProfileFields from "@/components/profile/ProfileFields";
import ServicesCard from "@/components/profile/ServicesCard";
import api from "@/lib/api";
import PushToggle from "@/components/notifications/PushToggle";
import { errorMessage } from "@/lib/errors";
import { useAuth } from "@/contexts/AuthContext";
import { deleteMemory, listMemories } from "@/lib/dany";
import { taxAuthorityFor } from "@/lib/taxAuthorities";

export default function Settings() {
  const { t } = useTranslation();
  const { user, refresh, logout } = useAuth();
  const role = user?.profile?.role === "provider" ? "provider" : "client";
  const [searchParams] = useSearchParams();

  const TABS = [
    { key: "profile", label: t("settings.tabs.profile"), icon: User },
    { key: "password", label: t("settings.tabs.password"), icon: Lock },
    { key: "notifications", label: t("settings.tabs.notifications"), icon: Bell },
    { key: "memory", label: t("settings.tabs.memory"), icon: Brain },
    { key: "danger", label: t("settings.tabs.danger"), icon: Trash2 },
  ];

  // `?tab=` opens the tab it names (R11, 2026-09-17).
  //
  // The footer of every transactional email links here with
  // `?tab=notifications`, and this page read the parameter nowhere — it always
  // opened on Profile. Fixing the route it points at without this would have
  // moved the link from a 404 to the wrong tab, which is the same errand
  // arriving one step short.
  //
  // Read once into the initial state rather than synced on every change: the
  // tab buttons below are the authority once someone is on the page, and a
  // `useEffect` mirroring the URL would fight them.
  const requested = searchParams.get("tab");
  const [tab, setTab] = useState(
    TABS.some((tb) => tb.key === requested) ? requested : "profile"
  );

  return (
    <DashboardLayout role={role}>
      <div data-testid="settings-page">
        <h1 className="font-display text-3xl md:text-4xl font-bold text-worker">{t("settings.title")}</h1>
        <p className="mt-2 text-smoke">{t("settings.subtitle")}</p>

        {/* This row is where ViewNav's shape came from (T13-M3) — it now
            lives in the component, with the sideways scroll the other two
            rows needed and this one needed just as much at five tabs. */}
        <ViewNav
          className="mt-6"
          testId="settings-tabs"
          items={TABS.map((tb) => ({
            key: tb.key, label: tb.label, icon: tb.icon, testId: `tab-${tb.key}`,
          }))}
          active={tab}
          onSelect={setTab}
        />

        <div className="mt-6">
          {tab === "profile" && <ProfileTab t={t} user={user} refresh={refresh} />}
          {tab === "profile" && role === "provider" && <ServicesCard />}
          {tab === "password" && <PasswordTab t={t} user={user} refresh={refresh} />}
          {tab === "notifications" && <NotificationsTab t={t} role={role} />}
          {tab === "memory" && <MemoryTab t={t} />}
          {tab === "danger" && <DangerTab t={t} user={user} logout={logout} />}
        </div>
      </div>
    </DashboardLayout>
  );
}

function ProfileTab({ t, user, refresh }) {
  const p = user?.profile || {};
  const [form, setForm] = useState({
    full_name: p.full_name || "",
    phone: p.phone || "",
    bio: p.bio || "",
    avatar_url: p.avatar_url || "",
    company_name: p.company_name || "",
    city: p.location?.city || "",
    state: p.location?.state || "",
    zip: p.location?.zip || "",
    // `payment_links` is gone from this form (T12-M4b). The editor it fed was
    // retired, and leaving the state behind meant every save PATCHed three
    // empty strings into the retired columns — a write to a shape nothing reads
    // any more, on a screen that no longer offers it.
    // N24. A string, not a number, so the box can be empty and mean "I charge
    // no sales tax" rather than snapping to 0 while somebody is still typing
    // "8.2". Same reasoning, and the same shape, as `SendQuote.jsx`.
    default_tax_rate:
      p.default_tax_rate === null || p.default_tax_rate === undefined
        ? ""
        : String(p.default_tax_rate),
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const save = async () => {
    setMsg(null);
    setSaving(true);
    try {
      // Empty means "no default rate", and it has to travel as an explicit
      // null: a field simply left out is a field the backend keeps, so
      // omitting it would make the box impossible to clear once filled.
      const rate = String(form.default_tax_rate ?? "").trim();
      await api.patch("/users/me", {
        ...form,
        default_tax_rate: rate === "" ? null : Number(rate),
      });
      if (refresh) await refresh();
      setMsg({ type: "ok", text: t("common.savedSuccess") });
    } catch (e) {
      setMsg({ type: "err", text: errorMessage(e, t("common.error")) });
    } finally {
      setSaving(false);
    }
  };

  const patch = (p) => setForm((f) => ({ ...f, ...p }));

  return (
    <div className="card-base p-6" data-testid="profile-tab">
      <h2 className="font-display text-xl font-bold text-worker mb-4">{t("settings.profile.heading")}</h2>

      {/* **Which account is this?** (R5) The page that lets you edit every
          other fact about yourself never said the one that identifies the
          session — so anyone with two accounts (Daniel's test pair, or a
          household with one each) could not tell from the page which one they
          were signed into. Read-only and first, above everything editable:
          changing an email is a different conversation, with a verification in
          it, so the field says where to ask rather than pretending to offer
          it. `user.email` is already in the document this page reads. */}
      <div className="mb-6 pb-6 border-b border-sand" data-testid="account-email-section">
        <label className="label-base">{t("settings.profile.accountEmail")}</label>
        <p className="input-base bg-linen text-worker font-mono text-sm truncate"
           data-testid="account-email">{user?.email || "—"}</p>
        <p className="text-xs text-smoke mt-1">{t("settings.profile.accountEmailHint")}</p>
      </div>

      <ProfileFields
        form={form}
        onChange={patch}
        role={user?.profile?.role === "provider" ? "provider" : "client"}
        phoneSlot={
          <Field label={t("settings.profile.phone")} value={form.phone}
            onChange={(v) => patch({ phone: v })} testid="profile-phone" hint={t("settings.profile.phoneHint")} />
        }
      />

      {/* The three https link fields that used to be here are retired (T12-M4b).
          They held a full URL for Cash App, Zelle and Square; payment contact is
          now a HANDLE per app, five apps, on its own screen. This is the link to
          it rather than a second editor — two places to set the same thing is
          how they disagree. */}
      {user?.profile?.role === "provider" && (
        <div className="mt-6 pt-6 border-t border-sand" data-testid="payment-links-section">
          <label className="label-base">{t("settings.profile.paymentLinks")}</label>
          <p className="text-xs text-smoke mt-1 mb-3">{t("payouts.context")}</p>
          <Link
            to="/panel/payment-methods"
            className="fx-btn fx-btn--secondary text-sm inline-flex items-center gap-2"
            data-testid="payment-methods-link"
          >
            {t("payouts.manage")} <ChevronRight size={14} />
          </Link>
        </div>
      )}

      {/* N24. "What I usually charge" is a fact about the FixyBee, so it lives
          with the rest of their account and not on one quote's form — the
          quote form offers it and lets them change it for that quote alone.
          Empty is a real answer and the one the column stores as NULL:
          "I never said" and "I charge zero" are different statements, which is
          why the box clears to null and not to 0.
          FixyDany publishes no rates. A platform that pre-fills one has told a
          tradesperson what to charge the government, so the link next to it
          goes to their own state's office — `taxAuthorityFor` never returns
          null, so it is never dead. */}
      {user?.profile?.role === "provider" && (
        <div className="mt-6 pt-6 border-t border-sand" data-testid="tax-rate-section">
          <label className="label-base" htmlFor="profile-tax-rate">
            {t("settings.profile.taxRate")}
          </label>
          <div className="flex items-center gap-2">
            <input
              id="profile-tax-rate"
              type="number" min="0" max="99.999" step="0.001"
              className="input-base max-w-[9rem]"
              placeholder="8.25"
              value={form.default_tax_rate}
              onChange={(v) => patch({ default_tax_rate: v.target.value })}
              data-testid="profile-tax-rate"
            />
            <span className="text-smoke">%</span>
          </div>
          <p className="text-xs text-smoke mt-1">
            <a href={taxAuthorityFor(form.state)} target="_blank" rel="noopener noreferrer"
               className="underline" data-testid="profile-tax-link">
              {t("settings.profile.taxLink")}
            </a>{" "}
            {t("settings.profile.taxRateHint")}
          </p>
        </div>
      )}

      {msg && (
        <div className={`mt-3 text-sm p-2 rounded-sm ${msg.type === "ok" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`}
          data-testid="profile-msg">
          {msg.type === "ok" ? <CheckCircle2 size={14} className="inline mr-1" /> : <AlertCircle size={14} className="inline mr-1" />}
          {msg.text}
        </div>
      )}
      <div className="mt-4 flex justify-end">
        <button onClick={save} disabled={saving} className="fx-btn fx-btn--primary" data-testid="profile-save">
          {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
          {t("common.save")}
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, mono, hint }) {
  return (
    <div>
      <label className="label-base">{label}</label>
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className={`input-base ${mono ? "font-mono text-sm" : ""}`}
        data-testid={testid}
      />
      {hint && <p className="text-xs text-smoke mt-1">{hint}</p>}
    </div>
  );
}

// One tab, two jobs, chosen by whether a password exists. An account created
// with Google has none, and until /auth/set-password it could not get one: this
// form asked it for a "current password" it had never had and the backend
// answered 400 every time. `has_password` is the signal and not `provider`,
// because setting a password does not change how the account was created.
function PasswordTab({ t, user, refresh }) {
  const hasPassword = user?.has_password !== false;
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const submit = async () => {
    setMsg(null);
    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/.test(next)) return setMsg({ type: "err", text: t("auth.passwordStrength") });
    if (next !== confirm) return setMsg({ type: "err", text: t("common.error") });
    setSaving(true);
    try {
      if (hasPassword) {
        await api.post("/auth/change-password", { current_password: current, new_password: next });
        setMsg({ type: "ok", text: t("common.savedSuccess") });
      } else {
        await api.post("/auth/set-password", { new_password: next });
        setMsg({ type: "ok", text: t("settings.password.setDone") });
        // The tab has to stop offering to set a password the moment one exists,
        // and `has_password` only changes in a re-fetched user document.
        if (refresh) await refresh();
      }
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      setMsg({ type: "err", text: errorMessage(e, t("common.error")) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-base p-6 max-w-lg" data-testid="password-tab">
      <h2 className="font-display text-xl font-bold text-worker mb-1">
        {hasPassword ? t("settings.password.heading") : t("settings.password.setHeading")}
      </h2>
      {!hasPassword && (
        <p className="text-sm text-smoke" data-testid="password-set-intro">{t("settings.password.setIntro")}</p>
      )}
      <div className="space-y-3 mt-4">
        {hasPassword && (
          <PwField label={t("settings.password.current")} value={current} onChange={setCurrent} testid="pw-current" />
        )}
        <PwField label={t("settings.password.new")} value={next} onChange={setNext} testid="pw-new" />
        <PwField label={t("settings.password.confirm")} value={confirm} onChange={setConfirm} testid="pw-confirm" />
      </div>
      {msg && (
        <div className={`mt-3 text-sm p-2 rounded-sm ${msg.type === "ok" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`}
          data-testid="password-msg">
          {msg.type === "ok" ? <CheckCircle2 size={14} className="inline mr-1" /> : <AlertCircle size={14} className="inline mr-1" />}
          {msg.text}
        </div>
      )}
      <div className="mt-4 flex justify-end">
        <button onClick={submit} disabled={saving || (hasPassword && !current) || !next} className="fx-btn fx-btn--primary" data-testid="password-save">
          {saving ? <Loader2 className="animate-spin" size={16} /> : <Lock size={16} />}
          {hasPassword ? t("settings.password.submit") : t("settings.password.setSubmit")}
        </button>
      </div>
    </div>
  );
}

function PwField({ label, value, onChange, testid }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="label-base">{label}</label>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="input-base pr-10"
          data-testid={testid}
        />
        <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-smoke hover:text-worker" data-testid={`${testid}-toggle`} tabIndex={-1}>
          {show ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
    </div>
  );
}

function NotificationsTab({ t, role }) {
  const [prefs, setPrefs] = useState(null);
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    api.get("/users/me/notification-preferences")
      .then(({ data }) => {
        setPrefs(data.prefs || {});
        setCategories(data.categories || []);
      })
      .catch(() => { setPrefs({}); setCategories([]); });
  }, []);

  const toggle = async (key) => {
    setMsg(null);
    const previous = prefs;
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    setSaving(true);
    try {
      await api.patch("/users/me/notification-preferences", { prefs: { [key]: next[key] } });
      setMsg({ type: "ok", text: t("common.savedSuccess") });
    } catch (e) {
      setMsg({ type: "err", text: errorMessage(e, t("common.error")) });
      setPrefs(previous);
    } finally {
      setSaving(false);
    }
  };

  if (!prefs) return <div className="card-base p-6"><Loader2 className="animate-spin mx-auto" /></div>;

  // The screen is grouped by SIDE OF THE MARKETPLACE, not by the account's
  // role. DY2.6 let a FixyBee post a job, so they really do receive the rows
  // labelled "when you ask for a service" — grouping by role would hide those
  // toggles from the people the notifications actually reach. The server sends
  // which sides this account sees; this file only renders them.
  const SIDES = ["requester", "provider", "both"];
  const bySide = SIDES
    .map((side) => [side, categories.filter((c) => c.side === side)])
    .filter(([, rows]) => rows.length > 0);

  // Marketing is transactional's opposite and is kept visually apart from it,
  // which is the whole reason it is pulled out of `both` rather than sorted
  // within it: a promotional switch sitting between two errand switches reads
  // as one more errand.
  const isMarketing = (c) => c.category === "marketing";

  return (
    <div className="card-base p-6 max-w-2xl" data-testid="notifications-tab">
      <h2 className="font-display text-xl font-bold text-worker mb-1">{t("settings.notifications.heading")}</h2>
      <p className="text-sm text-smoke mb-4">{t("settings.notifications.intro")}</p>

      {/* The same switch the dashboard carries, mirrored here beside the
          per-event preferences — which answer a different question. Those say
          WHICH notifications; this says whether this browser receives any at
          all, and a preference is moot on a device that is not subscribed. */}
      <div className="mb-5 pb-5 border-b border-sand">
        <PushToggle compact />
      </div>

      {bySide.map(([side, rows]) => {
        const transactional = rows.filter((c) => !isMarketing(c));
        if (transactional.length === 0) return null;
        return (
          <MatrixSection
            key={side}
            title={t(`settings.notifications.side.${side}`)}
            rows={transactional}
            prefs={prefs}
            onToggle={toggle}
            saving={saving}
            t={t}
          />
        );
      })}

      {categories.some(isMarketing) && (
        <MatrixSection
          title={t("settings.notifications.side.marketing")}
          rows={categories.filter(isMarketing)}
          prefs={prefs}
          onToggle={toggle}
          saving={saving}
          t={t}
        />
      )}

      {/* Stated in words because it is not a control. Account and security mail
          always arrives, and a disabled checkbox would invite people to keep
          clicking something that will never move. */}
      <div className="mt-6 pt-4 border-t border-sand" data-testid="always-on-note">
        <p className="font-display font-bold text-worker text-sm">
          {t("settings.notifications.side.account")}
        </p>
        <p className="text-xs text-smoke mt-1">{t("settings.notifications.alwaysOn")}</p>
      </div>

      {msg && (
        <div className={`mt-4 text-xs ${msg.type === "ok" ? "text-green-700" : "text-red-700"}`} data-testid="notif-msg">
          {msg.text}
        </div>
      )}
    </div>
  );
}

/**
 * One section of the matrix: a heading and its rows, each row an event with a
 * checkbox per channel it offers.
 *
 * Mobile-first, and the two layouts are genuinely different rather than one
 * squeezed. On a phone the channel switches sit under the row's own text, where
 * a thumb reaches them and the label has the full width to say what the event
 * is. From `sm` up the same data becomes a grid with the channel names as
 * column headers, which is the shape the matrix actually has and the shape that
 * makes "push but not email" readable at a glance.
 */
function MatrixSection({ title, rows, prefs, onToggle, saving, t }) {
  const CHANNELS = ["push", "email"];
  return (
    <div className="mt-5 first:mt-0" data-testid={`pref-section-${title}`}>
      <p className="font-display font-bold text-worker mb-1">{title}</p>

      <div className="hidden sm:grid grid-cols-[1fr_auto_auto] gap-x-4 pb-1 border-b border-sand">
        <span />
        {CHANNELS.map((ch) => (
          <span key={ch} className="text-[11px] uppercase tracking-widest text-smoke font-display w-12 text-center">
            {t(`settings.notifications.channel.${ch}`)}
          </span>
        ))}
      </div>

      <div className="divide-y divide-sand">
        {rows.map((row) => (
          <div
            key={row.category}
            className="py-3 sm:grid sm:grid-cols-[1fr_auto_auto] sm:gap-x-4 sm:items-center"
            data-testid={`pref-row-${row.category}`}
          >
            <div>
              <p className="text-sm text-worker">{t(`settings.notifications.event.${row.category}.label`)}</p>
              <p className="text-xs text-smoke">{t(`settings.notifications.event.${row.category}.hint`)}</p>
            </div>

            <div className="flex gap-6 mt-2 sm:hidden">
              {CHANNELS.map((ch) => (
                <ChannelToggle
                  key={ch} row={row} channel={ch} prefs={prefs}
                  onToggle={onToggle} saving={saving} t={t} withLabel
                />
              ))}
            </div>

            {CHANNELS.map((ch) => (
              <div key={ch} className="hidden sm:flex w-12 justify-center">
                <ChannelToggle
                  row={row} channel={ch} prefs={prefs}
                  onToggle={onToggle} saving={saving} t={t}
                />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/** One cell. Renders nothing where the matrix offers no toggle. */
function ChannelToggle({ row, channel, prefs, onToggle, saving, t, withLabel }) {
  if (!row.channels.includes(channel)) return <span className="w-12" />;
  const key = `${channel}_${row.category}`;
  const label = t(`settings.notifications.channel.${channel}`);
  return (
    <label className="flex items-center gap-2 cursor-pointer" data-testid={`pref-${key}`}>
      <input
        type="checkbox"
        checked={!!prefs[key]}
        onChange={() => onToggle(key)}
        disabled={saving}
        className="w-4 h-4 accent-hive-deep"
        aria-label={`${t(`settings.notifications.event.${row.category}.label`)} — ${label}`}
      />
      {withLabel && <span className="text-xs text-smoke">{label}</span>}
    </label>
  );
}

/**
 * What Dany remembers — DANY.md 2.4's non-negotiable requirement.
 *
 * The list is Dany's own sentences, verbatim and in the language of the
 * conversation they came from: they are what actually reaches the model, and
 * showing a prettier paraphrase would mean the screen and the prompt disagree
 * about what the platform knows. Deleting is immediate and one-way here; the
 * copy says plainly that it applies to the next conversation, because the one
 * open in another tab already has its context.
 */
function MemoryTab({ t }) {
  const [memories, setMemories] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    let alive = true;
    listMemories()
      .then((rows) => { if (alive) setMemories(rows); })
      .catch((e) => { if (alive) { setError(errorMessage(e, t("common.error"))); setMemories([]); } });
    return () => { alive = false; };
  }, [t]);

  const forget = async (id) => {
    setBusy(id);
    setError(null);
    try {
      await deleteMemory(id);
      setMemories((rows) => rows.filter((r) => r.memory_id !== id));
    } catch (e) {
      setError(errorMessage(e, t("common.error")));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="card-base p-6 max-w-2xl" data-testid="memory-tab">
      <h2 className="font-display text-xl font-bold text-worker mb-1">{t("settings.memory.heading")}</h2>
      <p className="text-sm text-smoke mb-4">{t("settings.memory.intro")}</p>

      {memories === null && (
        <div className="flex items-center gap-2 text-smoke text-sm py-6">
          <Loader2 className="animate-spin" size={16} /> {t("common.loading")}
        </div>
      )}

      {memories !== null && memories.length === 0 && !error && (
        <p className="text-sm text-smoke py-6" data-testid="memory-empty">
          {t("settings.memory.empty")}
        </p>
      )}

      {memories !== null && memories.length > 0 && (
        <ul className="space-y-2" data-testid="memory-list">
          {memories.map((m) => (
            <li
              key={m.memory_id}
              className="flex items-start justify-between gap-3 p-3 rounded-sm border border-sand bg-paper"
              data-testid={`memory-${m.memory_id}`}
            >
              <span className="text-sm text-worker leading-relaxed">{m.content}</span>
              <button
                onClick={() => forget(m.memory_id)}
                disabled={busy === m.memory_id}
                className="shrink-0 text-smoke hover:text-red-700 p-1 rounded-sm"
                aria-label={t("settings.memory.forget")}
                title={t("settings.memory.forget")}
                data-testid={`memory-forget-${m.memory_id}`}
              >
                {busy === m.memory_id ? <Loader2 className="animate-spin" size={16} /> : <X size={16} />}
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && (
        <p className="mt-3 text-xs text-red-700" data-testid="memory-error">{error}</p>
      )}
      <p className="mt-4 text-xs text-smoke">{t("settings.memory.note")}</p>
    </div>
  );
}


function DangerTab({ t, user, logout }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card-base p-6 max-w-lg border border-red-200" data-testid="danger-tab">
      <h2 className="font-display text-xl font-bold text-red-700 flex items-center gap-2">
        <ShieldAlert size={20} /> {t("settings.danger.heading")}
      </h2>
      <p className="mt-2 text-sm text-smoke">{t("settings.danger.warn")}</p>
      <button
        onClick={() => setOpen(true)}
        className="fx-btn fx-btn--secondary mt-4 border-red-300 text-red-700 hover:bg-red-50"
        data-testid="open-delete-modal"
      >
        <Trash2 size={14} /> {t("settings.danger.submit")}
      </button>
      {open && <DeleteModal t={t} user={user} onClose={() => setOpen(false)} onDeleted={logout} />}
    </div>
  );
}

function DeleteModal({ t, user, onClose, onDeleted }) {
  const navigate = useNavigate();
  // `user.is_google_user` never existed: the document carries `provider` and,
  // since /auth/set-password, `has_password`. The old expression was `!undefined`
  // — permanently true — so a Google-only account was asked to confirm a deletion
  // with a password it did not have. The request went through regardless (the
  // route gates on the row, users.py), so what this cost was a field nobody
  // could fill and the fear that goes with it.
  const hasPassword = user?.has_password !== false;
  const [password, setPassword] = useState("");
  const [phrase, setPhrase] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPw, setShowPw] = useState(false);

  const submit = async () => {
    setError("");
    if (phrase !== "DELETE MY ACCOUNT") {
      setError(t("settings.danger.confirm"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/users/me/delete", {
        password: hasPassword ? password : null,
        confirm_phrase: phrase,
      });
      if (onDeleted) await onDeleted();
      navigate("/", { replace: true });
    } catch (e) {
      setError(errorMessage(e, t("common.error")));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-worker/70 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" data-testid="delete-modal">
      <div className="bg-cream rounded-md max-w-md w-full p-6 relative">
        <button onClick={onClose} className="absolute top-3 right-3 text-smoke hover:text-worker">
          <X size={20} />
        </button>
        <h3 className="font-display text-2xl font-bold text-red-700 mb-2">{t("settings.danger.heading")}</h3>
        <p className="text-sm text-smoke mb-4">{t("settings.danger.warn")}</p>
        {hasPassword && (
          <div className="mb-3">
            <label className="label-base">{t("settings.password.current")}</label>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-base pr-10"
                data-testid="delete-password"
              />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-smoke hover:text-worker" data-testid="delete-pw-toggle" tabIndex={-1}>
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
        )}
        <div className="mb-3">
          <label className="label-base">{t("settings.danger.confirm")}</label>
          <input
            value={phrase}
            onChange={(e) => setPhrase(e.target.value)}
            className="input-base font-mono"
            data-testid="delete-phrase"
          />
        </div>
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-sm p-2 text-sm text-red-700 mb-3" data-testid="delete-error">
            <AlertCircle size={14} className="inline mr-1" /> {error}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="fx-btn fx-btn--secondary">{t("common.cancel")}</button>
          <button
            onClick={submit}
            disabled={loading}
            className="fx-btn fx-btn--primary bg-red-600 hover:bg-red-700"
            data-testid="confirm-delete-btn"
          >
            {loading ? <Loader2 className="animate-spin" size={16} /> : <Trash2 size={16} />}
            {t("settings.danger.submit")}
          </button>
        </div>
      </div>
    </div>
  );
}
